import React, { useEffect, useState } from "react";
import { useFileProgress } from "@/context/FileProgressContext";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * FileProgressBar — fixed, fade-up progress strip pinned to the bottom of
 * the viewport. Visible only when at least one task is active.
 *
 * Visual contract:
 *   • Slides up + fades in from below the fold when a task starts.
 *   • Renders the most-recent task's label + numeric percent (when known).
 *   • Indeterminate state (no Content-Length): animated stripe shimmer.
 *   • On `complete` the bar finishes to 100 %, then fades out after 700 ms.
 *   • On `error` switches to a destructive tone and auto-dismisses.
 *
 * Stays out of the way:
 *   • `pointer-events-none` on the outer wrapper so the bar never blocks
 *     UI underneath (e.g. modal close buttons).
 *   • z-index above modals (z-[60]) so it remains visible during view fetch.
 */
export default function FileProgressBar() {
  const { tasks } = useFileProgress();
  // The freshest task is the most user-relevant.
  const task = tasks.length ? tasks[tasks.length - 1] : null;
  // Keep a "shown" flag so we can play the slide-out animation cleanly
  // after the task is removed from context.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (task) {
      setVisible(true);
      return;
    }
    // Delay un-mount slightly so the exit transition has room to play.
    const id = setTimeout(() => setVisible(false), 320);
    return () => clearTimeout(id);
  }, [task]);

  if (!visible && !task) return null;

  const tone =
    task?.status === "error"
      ? "border-destructive/40 bg-destructive/5"
      : task?.status === "complete"
      ? "border-emerald-500/40 bg-emerald-500/5"
      : "border-border bg-background/95";

  const barColor =
    task?.status === "error"
      ? "bg-destructive"
      : task?.status === "complete"
      ? "bg-emerald-500"
      : "bg-primary";

  const Icon =
    task?.status === "error" ? AlertCircle
    : task?.status === "complete" ? CheckCircle2
    : Loader2;

  const label = task?.error || task?.label || "Preparing file…";
  const pct = task?.percent ?? 0;
  const isIndeterminate = task?.indeterminate && task?.status === "loading";

  return (
    <div
      data-testid="file-progress-bar"
      data-status={task?.status || "idle"}
      className={`fixed inset-x-0 bottom-0 z-[60] pointer-events-none px-3 pb-3 sm:px-6 sm:pb-6 flex justify-center`}
      aria-live="polite"
    >
      <div
        className={`w-full max-w-xl rounded-md border ${tone} backdrop-blur-sm shadow-lg transition-all duration-300 ease-out ${
          task ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        <div className="flex items-center gap-3 px-4 py-2.5">
          <Icon
            className={`w-4 h-4 flex-shrink-0 ${
              task?.status === "loading" ? "animate-spin text-primary"
              : task?.status === "complete" ? "text-emerald-500"
              : "text-destructive"
            }`}
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <span
                className="text-xs sm:text-sm truncate text-foreground font-medium"
                title={label}
                data-testid="file-progress-label"
              >
                {label}
              </span>
              {task?.status === "loading" && !isIndeterminate && (
                <span
                  className="label-mono text-muted-foreground tabular-nums"
                  data-testid="file-progress-percent"
                >
                  {pct}%
                </span>
              )}
              {task?.status === "complete" && (
                <span className="label-mono text-emerald-600 tabular-nums">Ready</span>
              )}
            </div>
            {/* Track */}
            <div className="mt-2 h-1 w-full rounded-full bg-muted overflow-hidden">
              {isIndeterminate ? (
                <div
                  className={`h-full w-1/3 rounded-full ${barColor} file-progress-indeterminate`}
                />
              ) : (
                <div
                  className={`h-full rounded-full ${barColor} transition-[width] duration-200 ease-out`}
                  style={{ width: `${task?.status === "complete" ? 100 : pct}%` }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
